import React, { useEffect, useRef, useState } from 'react';
import './Notifications.scss';
import Notification from '../Notification';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { createPortal } from 'react-dom';
import { INotification } from './INotification';
import clsx from 'clsx';
import { getNotifications$, removeNotification, resetNotifications$ } from '@utils/notifications';

// ----Компонент--------------------------------------------------------------------------------------------------------

export interface INotificationsProps {
    /** класс для управления расположением сообщений */
    className?: string;
}

interface INotificationWithState extends INotification {
    state: 'entering' | 'entered' | 'exiting';
}

const Notifications = ({ className }: INotificationsProps) => {
    /** Флаг по которому остановить подписку */
    const obstacle = useRef<Subject<boolean>>(new Subject());

    const [sub, setSub] = useState<BehaviorSubject<INotification[]> | null>(null);

    useEffect(() => {
        resetNotifications$();

        const sub = getNotifications$();

        setSub(sub);
    }, []);

    /** Список уведомлений с состояниями анимации */
    const [notifications, setNotifications] = useState<INotificationWithState[]>([]);

    // Таймауты для управления анимацией
    const timeoutsRef = useRef<Map<string, number>>(new Map());

    // -------------------------------------------------------------------------------------------------------------------

    /** Подписываемся на список уведомлений */
    useEffect(() => {
        if (!sub || sub.closed) {
            return;
        }

        const until = obstacle.current;

        sub.pipe(takeUntil(until)).subscribe((data: INotification[]) => {
            setNotifications((prev) => {
                const newNotifications = [...prev];
                const prevIds = new Set(prev.map((n) => n.id));
                const currentIds = new Set(data.map((n) => n.id));

                // Находим новые уведомления
                data.forEach((notification) => {
                    if (!prevIds.has(notification.id)) {
                        // Добавляем новое уведомление в состоянии 'entering'
                        newNotifications.push({
                            ...notification,
                            state: 'entering',
                        });

                        // Через небольшую задержку переводим в 'entered'
                        const timeout = setTimeout(() => {
                            setNotifications((current) =>
                                current.map((n) => (n.id === notification.id ? { ...n, state: 'entered' } : n)),
                            );
                            timeoutsRef.current.delete(notification.id!);
                        }, 10);

                        if (notification.id) {
                            timeoutsRef.current.set(notification.id, timeout);
                        }
                    }
                });

                // Находим удаленные уведомления и переводим их в 'exiting'
                prev.forEach((notification) => {
                    if (!currentIds.has(notification.id) && notification.state !== 'exiting') {
                        const index = newNotifications.findIndex((n) => n.id === notification.id);
                        if (index !== -1) {
                            newNotifications[index] = {
                                ...newNotifications[index],
                                state: 'exiting',
                            };

                            // Удаляем уведомление после завершения анимации
                            const timeout = setTimeout(() => {
                                setNotifications((current) => current.filter((n) => n.id !== notification.id));
                                timeoutsRef.current.delete(notification.id!);
                            }, 300); // Длительность анимации выхода

                            if (notification.id) {
                                timeoutsRef.current.set(notification.id, timeout);
                            }
                        }
                    }
                });

                return newNotifications;
            });
        });

        return () => {
            until.next(true);
            // Очищаем все таймауты при размонтировании
            timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
            timeoutsRef.current.clear();
        };
    }, [sub]);

    // -------------------------------------------------------------------------------------------------------------------
    /** Список уведомлений TSX */
    const list = notifications.map((n: INotificationWithState, i: number) => (
        <Notification key={n.id || i} item={n} remove={removeNotification} animationState={n.state} />
    ));

    // -------------------------------------------------------------------------------------------------------------------

    if (!list.length) {
        return null;
    }

    return createPortal(
        <div data-testid="rf-notifications" className={clsx('rf-notifications__list', className)}>
            {list}
        </div>,
        document.body,
    );
};

export default Notifications;
